import {
  WorkflowTriggerEntityType,
  WorkflowTriggerEventType,
  type WorkflowTriggerEventSchema,
} from '@/gen/cortado/v1/cortado_pb'

export type TriggerEventMetadata = {
  entity: WorkflowTriggerEntityType
  label: string
  description: string
}

/** Option shape consumed by the trigger picker's cascade selects. */
export type TriggerEventOption = {
  eventType: WorkflowTriggerEventType
  entityType: WorkflowTriggerEntityType
  label: string
  description: string
}

/**
 * The workflow API still uses Inquiry as its compatibility term. Translate
 * server-authored copy at the UI boundary so customers consistently see Lead.
 */
function workflowDisplayCopy(value: string): string {
  return value
    .replace(/\bInquiries\b/g, 'Leads')
    .replace(/\binquiries\b/g, 'leads')
    .replace(/\bInquiry\b/g, 'Lead')
    .replace(/\binquiry\b/g, 'lead')
}

// EVENT_METADATA is the fallback metadata store for event labels, entity
// grouping, and tooltip descriptions when the server schema is unavailable.
// The server schema (getWorkflowAuthoringSchema) is the source of truth for
// which events are selectable; this metadata is used for loading/error states.
export const EVENT_METADATA: Record<WorkflowTriggerEventType, TriggerEventMetadata> = {
  [WorkflowTriggerEventType.INQUIRY_CREATED]: {
    entity: WorkflowTriggerEntityType.INQUIRY,
    label: 'Lead created',
    description: 'Fires when a new lead is submitted.',
  },
  [WorkflowTriggerEventType.INQUIRY_STAGE_CHANGED]: {
    entity: WorkflowTriggerEntityType.INQUIRY,
    label: 'Lead stage changed',
    description: 'Fires when a lead moves to a different pipeline stage.',
  },
  [WorkflowTriggerEventType.INQUIRY_ASSIGNED]: {
    entity: WorkflowTriggerEntityType.INQUIRY,
    label: 'Lead assigned',
    description: 'Fires when a lead is assigned to a team member.',
  },
  [WorkflowTriggerEventType.QUOTE_CREATED]: {
    entity: WorkflowTriggerEntityType.QUOTE,
    label: 'Quote created',
    description: 'Fires when a new quote is created for a client.',
  },
  [WorkflowTriggerEventType.QUOTE_SENT]: {
    entity: WorkflowTriggerEntityType.QUOTE,
    label: 'Quote sent',
    description: 'Fires when a quote is sent to the client.',
  },
  [WorkflowTriggerEventType.QUOTE_VIEWED]: {
    entity: WorkflowTriggerEntityType.QUOTE,
    label: 'Quote viewed',
    description: 'Fires when a client opens and views a quote.',
  },
  [WorkflowTriggerEventType.QUOTE_ACCEPTED]: {
    entity: WorkflowTriggerEntityType.QUOTE,
    label: 'Quote accepted',
    description: 'Fires when a client accepts a quote.',
  },
  [WorkflowTriggerEventType.QUOTE_REJECTED]: {
    entity: WorkflowTriggerEntityType.QUOTE,
    label: 'Quote rejected',
    description: 'Fires when a client declines a quote.',
  },
  [WorkflowTriggerEventType.QUOTE_EXPIRING_SOON]: {
    entity: WorkflowTriggerEntityType.QUOTE,
    label: 'Quote expiring soon',
    description:
      'Fires a set number of days before the quote reaches its expiration date.',
  },
  [WorkflowTriggerEventType.QUOTE_EXPIRED]: {
    entity: WorkflowTriggerEntityType.QUOTE,
    label: 'Quote expired',
    description: 'Fires when a quote passes its expiration date without a response.',
  },
  [WorkflowTriggerEventType.INVOICE_SENT]: {
    entity: WorkflowTriggerEntityType.INVOICE,
    label: 'Invoice sent',
    description: 'Fires when an invoice is sent to the client.',
  },
  [WorkflowTriggerEventType.INVOICE_PAID]: {
    entity: WorkflowTriggerEntityType.INVOICE,
    label: 'Invoice paid',
    description: 'Fires when an invoice is marked as fully paid.',
  },
  [WorkflowTriggerEventType.INVOICE_OVERDUE]: {
    entity: WorkflowTriggerEntityType.INVOICE,
    label: 'Invoice overdue',
    description: 'Fires when an invoice passes its due date without full payment.',
  },
  [WorkflowTriggerEventType.PAYMENT_RECEIVED]: {
    entity: WorkflowTriggerEntityType.PAYMENT,
    label: 'Payment received',
    description: 'Fires when a payment is successfully collected.',
  },
  [WorkflowTriggerEventType.PAYMENT_FAILED]: {
    entity: WorkflowTriggerEntityType.PAYMENT,
    label: 'Payment failed',
    description: 'Fires when a payment attempt fails or is declined.',
  },
  [WorkflowTriggerEventType.EVENT_CREATED]: {
    entity: WorkflowTriggerEntityType.EVENT,
    label: 'Event created',
    description: 'Fires when a new event is created.',
  },
  [WorkflowTriggerEventType.EVENT_COMPLETED]: {
    entity: WorkflowTriggerEntityType.EVENT,
    label: 'Event completed',
    description: 'Fires when an event is marked as completed.',
  },
  [WorkflowTriggerEventType.EVENT_CANCELED]: {
    entity: WorkflowTriggerEntityType.EVENT,
    label: 'Event canceled',
    description: 'Fires when an event is canceled.',
  },
  [WorkflowTriggerEventType.CONTACT_CREATED]: {
    entity: WorkflowTriggerEntityType.CONTACT,
    label: 'Contact created',
    description: 'Fires when a new contact record is created.',
  },
  [WorkflowTriggerEventType.BOOKING_CREATED]: {
    entity: WorkflowTriggerEntityType.BOOKING,
    label: 'Booking created',
    description: 'Fires when a booking is created, including via quote acceptance.',
  },
  [WorkflowTriggerEventType.BOOKING_STAGE_CHANGED]: {
    entity: WorkflowTriggerEntityType.BOOKING,
    label: 'Booking stage changed',
    description: 'Fires when a booking moves to a different pipeline stage.',
  },
  [WorkflowTriggerEventType.INQUIRY_EMAIL_RECEIVED]: {
    entity: WorkflowTriggerEntityType.INQUIRY,
    label: 'Email received',
    description:
      'Fires when the customer emails on a thread linked to this lead. Automatic replies do not count.',
  },
  [WorkflowTriggerEventType.INQUIRY_NO_REPLY_FOR]: {
    entity: WorkflowTriggerEntityType.INQUIRY,
    label: 'No reply for N days',
    description:
      'Fires once when our last email on a lead has gone unanswered for the number of days you choose.',
  },
  [WorkflowTriggerEventType.BOOKING_EMAIL_RECEIVED]: {
    entity: WorkflowTriggerEntityType.BOOKING,
    label: 'Email received',
    description:
      'Fires when the customer emails on a thread linked to this booking. Automatic replies do not count.',
  },
  [WorkflowTriggerEventType.CONTACT_EMAIL_RECEIVED]: {
    entity: WorkflowTriggerEntityType.CONTACT,
    label: 'Email received',
    description:
      'Fires when this contact emails one of your connected inboxes. Automatic replies do not count.',
  },
  // UNSPECIFIED must be present for the Record type to be exhaustive
  [WorkflowTriggerEventType.UNSPECIFIED]: {
    entity: WorkflowTriggerEntityType.UNSPECIFIED,
    label: '',
    description: '',
  },
}

// ENTITY_LABELS maps entity types to display names for the entity select step.
export const ENTITY_LABELS: Partial<Record<WorkflowTriggerEntityType, string>> = {
  [WorkflowTriggerEntityType.INQUIRY]: 'Lead',
  [WorkflowTriggerEntityType.QUOTE]: 'Quote',
  [WorkflowTriggerEntityType.INVOICE]: 'Invoice',
  [WorkflowTriggerEntityType.PAYMENT]: 'Payment',
  [WorkflowTriggerEntityType.CONTACT]: 'Contact',
  [WorkflowTriggerEntityType.BOOKING]: 'Booking',
  [WorkflowTriggerEntityType.EVENT]: 'Event',
}

// SELECTABLE_EVENTS is the hardcoded fallback list of published events.
// The server schema (WorkflowTriggerEventSchema.publisherWired) is the
// authoritative source; this list is used for loading/error states and must
// mirror the backend's publisherWiredEvents set (authoring_schema.go).
export const SELECTABLE_EVENTS: WorkflowTriggerEventType[] = [
  WorkflowTriggerEventType.INQUIRY_CREATED,
  WorkflowTriggerEventType.INQUIRY_STAGE_CHANGED,
  WorkflowTriggerEventType.QUOTE_SENT,
  WorkflowTriggerEventType.QUOTE_VIEWED,
  WorkflowTriggerEventType.QUOTE_ACCEPTED,
  WorkflowTriggerEventType.QUOTE_EXPIRING_SOON,
  WorkflowTriggerEventType.QUOTE_EXPIRED,
  WorkflowTriggerEventType.PAYMENT_RECEIVED,
  WorkflowTriggerEventType.INVOICE_OVERDUE,
  WorkflowTriggerEventType.CONTACT_CREATED,
  WorkflowTriggerEventType.BOOKING_CREATED,
  WorkflowTriggerEventType.BOOKING_STAGE_CHANGED,
  WorkflowTriggerEventType.EVENT_CREATED,
  WorkflowTriggerEventType.INQUIRY_EMAIL_RECEIVED,
  WorkflowTriggerEventType.INQUIRY_NO_REPLY_FOR,
  WorkflowTriggerEventType.BOOKING_EMAIL_RECEIVED,
  WorkflowTriggerEventType.CONTACT_EMAIL_RECEIVED,
]

/**
 * Events that only make sense with the Connected inbox add-on: without it no
 * customer reply is captured, so "Email received" never happens and "No reply
 * for N days" would fire on every lead we wrote to. The backend also skips
 * publishing them for orgs without the add-on.
 */
export const CONNECTED_INBOX_EVENTS: ReadonlySet<WorkflowTriggerEventType> = new Set([
  WorkflowTriggerEventType.INQUIRY_EMAIL_RECEIVED,
  WorkflowTriggerEventType.BOOKING_EMAIL_RECEIVED,
  WorkflowTriggerEventType.CONTACT_EMAIL_RECEIVED,
  WorkflowTriggerEventType.INQUIRY_NO_REPLY_FOR,
])

/**
 * offeredEvents drops the Connected inbox events for orgs without the add-on,
 * keeping `keep` (the event already saved on the workflow) so a stored choice
 * still shows.
 */
export function offeredEvents<T>(
  events: T[],
  eventTypeOf: (event: T) => WorkflowTriggerEventType,
  hasConnectedInbox: boolean,
  keep?: WorkflowTriggerEventType
): T[] {
  if (hasConnectedInbox) return events
  return events.filter((event) => {
    const type = eventTypeOf(event)
    return type === keep || !CONNECTED_INBOX_EVENTS.has(type)
  })
}

// Lead time (days_before) bounds for relative-time triggers. MAX mirrors the
// backend's 90-day scheduling horizon (the same cap the WAIT node uses).
export const MIN_TRIGGER_DAYS_BEFORE = 1
export const MAX_TRIGGER_DAYS_BEFORE = 90
export const DEFAULT_TRIGGER_DAYS_BEFORE = 3
export const DEFAULT_NO_REPLY_DAYS = 14

/**
 * triggerRequiresDaysBefore reports whether an event fires a number of days
 * from a date and therefore needs days_before: "Quote expiring soon" (days
 * before expiry) and "No reply for N days" (days since our last email). Every
 * other event fires the moment it happens and must send days_before = 0.
 */
export function triggerRequiresDaysBefore(eventType: WorkflowTriggerEventType): boolean {
  return (
    eventType === WorkflowTriggerEventType.QUOTE_EXPIRING_SOON ||
    eventType === WorkflowTriggerEventType.INQUIRY_NO_REPLY_FOR
  )
}

/** defaultTriggerDaysBefore is the days_before a newly picked event starts with. */
export function defaultTriggerDaysBefore(eventType: WorkflowTriggerEventType): number {
  if (eventType === WorkflowTriggerEventType.INQUIRY_NO_REPLY_FOR) return DEFAULT_NO_REPLY_DAYS
  return triggerRequiresDaysBefore(eventType) ? DEFAULT_TRIGGER_DAYS_BEFORE : 0
}

/** triggerDaysBeforeLabel is the field label for the event's days input. */
export function triggerDaysBeforeLabel(eventType: WorkflowTriggerEventType): string {
  if (eventType === WorkflowTriggerEventType.INQUIRY_NO_REPLY_FOR) {
    return 'Run after this many days without a reply'
  }
  return 'Send this many days before the quote expires'
}

/**
 * triggerDaysBeforeError returns the human-readable problem with a trigger's
 * lead time, or undefined when it is valid (or the event doesn't use one).
 * Shared by the trigger picker (inline message) and validateWorkflowDefinition
 * (blocks publish) so both state the same rule.
 */
export function triggerDaysBeforeError(
  eventType: WorkflowTriggerEventType,
  daysBefore: number
): string | undefined {
  if (!triggerRequiresDaysBefore(eventType)) return undefined
  if (
    !Number.isInteger(daysBefore) ||
    daysBefore < MIN_TRIGGER_DAYS_BEFORE ||
    daysBefore > MAX_TRIGGER_DAYS_BEFORE
  ) {
    if (eventType === WorkflowTriggerEventType.INQUIRY_NO_REPLY_FOR) {
      return `Enter how many days without a reply to wait (${MIN_TRIGGER_DAYS_BEFORE}–${MAX_TRIGGER_DAYS_BEFORE})`
    }
    return `Enter how many days before the quote expires to run this (${MIN_TRIGGER_DAYS_BEFORE}–${MAX_TRIGGER_DAYS_BEFORE})`
  }
  return undefined
}

// SELECTABLE_ENTITIES lists entities that have at least one emitted event,
// in display order. Used as fallback when server schema is unavailable.
export const SELECTABLE_ENTITIES: WorkflowTriggerEntityType[] = [
  WorkflowTriggerEntityType.INQUIRY,
  WorkflowTriggerEntityType.QUOTE,
  WorkflowTriggerEntityType.INVOICE,
  WorkflowTriggerEntityType.PAYMENT,
  WorkflowTriggerEntityType.CONTACT,
  WorkflowTriggerEntityType.BOOKING,
  WorkflowTriggerEntityType.EVENT,
]

// eventsForEntity returns emitted event types that belong to a given entity.
// Uses the hardcoded SELECTABLE_EVENTS as a fallback.
export function eventsForEntity(
  entity: WorkflowTriggerEntityType
): WorkflowTriggerEventType[] {
  return SELECTABLE_EVENTS.filter((eventType) => EVENT_METADATA[eventType].entity === entity)
}

/**
 * Maps server WorkflowTriggerEventSchema items into TriggerEventOption objects
 * suitable for the picker UI. Combines server-provided labels/descriptions with
 * the entityType from the schema.
 *
 * Only call this with publisherWired=true events (filter with
 * selectableTriggerEvents from workflow-authoring.store before passing in).
 */
export function schemaEventsToOptions(
  events: WorkflowTriggerEventSchema[]
): TriggerEventOption[] {
  return events.map((event) => {
    const label =
      event.label || EVENT_METADATA[event.eventType]?.label || String(event.eventType)
    const description =
      event.description || EVENT_METADATA[event.eventType]?.description || ''

    return {
      eventType: event.eventType,
      entityType: event.entityType,
      label: workflowDisplayCopy(label),
      description: workflowDisplayCopy(description),
    }
  })
}

/**
 * Groups TriggerEventOptions by entityType.
 * Returns a Map from entityType to its events, preserving insertion order.
 */
export function groupOptionsByEntity(
  options: TriggerEventOption[]
): Map<WorkflowTriggerEntityType, TriggerEventOption[]> {
  const map = new Map<WorkflowTriggerEntityType, TriggerEventOption[]>()
  for (const option of options) {
    const group = map.get(option.entityType) ?? []
    group.push(option)
    map.set(option.entityType, group)
  }
  return map
}
